import storage from '#/common/utils/storage';
import { STORAGE } from '#/config/consts';
import IsObject from '#/types/IsObject';
import pick from 'lodash/pick';
import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ColumnProps } from './DataGridProps';
import useSession from '#/common/hooks/useSession';
import { debounce } from 'lodash';
import useTraceUpdate from '#/common/hooks/useTraceUpdate';
import { fixDateTZ } from './utils';

type State<T> = {
  [K in keyof T]?: IsObject<T[K], State<T[K]>, null | T[K]>;
};

export type Filter = unknown[];

interface Column {
  dataField?: string;
  dataType?: string;
  filterType?: string;
  filterValue?: unknown;
  filterValues?: unknown[];
  fixed?: boolean;
  fixedPosition?: string;
  groupIndex?: number;
  name?: string;
  selectedFilterOperation?: string;
  sortIndex?: number;
  sortOrder?: string;
  visible?: boolean;
  visibleIndex?: number;
  width?: number | string;
}

/**
 * https://js.devexpress.com/React/Documentation/ApiReference/UI_Components/dxDataGrid/Configuration/stateStoring/
 */
export type GridState = State<{
  filterValue: Filter;
  focusedRowKey: string;
  selectedRowKeys: string[];
  selectionFilter: Filter;
  filterPanel: {
    filterEnabled: boolean;
  };
  pageSize: number;
  pageIndex: number;
  searchText: string;
  columns: Column[];
  allowedPageSizes: number[];
}>;

const persistedKeys: (keyof GridState)[] = ['pageSize'];

const persistedColKeys: (keyof Column)[] = [
  'dataField',
  'dataType',
  'name',
  'sortIndex',
  'sortOrder',
  'visible',
  'visibleIndex',
  'width',
];

const store = storage(localStorage);

const findCol = (cols: State<Column | undefined | null>[] | null | undefined, dataField: string) =>
  cols?.find((c) => c?.dataField === dataField) ?? {};

/**
 * Read filters from URL query parameter
 * URL format: ?filters=[["name","contains","test"],"AND",["status","=","active"]]
 */
const readFiltersFromURL = (searchParams: URLSearchParams): Partial<GridState> => {
  try {
    const filtersParam = searchParams.get('filters');

    if (!filtersParam) {
      return {};
    }

    const decoded = decodeURIComponent(filtersParam);
    const parsed = JSON.parse(decoded);

    if (Array.isArray(parsed)) {
      return { filterValue: parsed };
    }

    return {};
  } catch (error) {
    console.warn('Failed to parse filters from URL', error);
    return {};
  }
};

/**
 * Write filters to URL query parameter in simple array format
 * URL format: ?filters=[["field","operator","value"]]
 */
const writeFiltersToURL = <T>(
  filterValue: Filter | null | undefined,
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams, options?: { replace?: boolean }) => void,
  columns: ColumnProps<T>[],
) => {
  try {
    const newParams = new URLSearchParams(searchParams);

    if (filterValue && Array.isArray(filterValue) && filterValue.length > 0) {
      // Write filter array directly (no wrapper object)
      const filtersJson = JSON.stringify(filterValue, (key, value) => {
        // is in format: [field, operator, value]
        const isFilter = Array.isArray(value) && value.length === 3 && typeof value[0] === 'string';
        if (!isFilter) {
          return value;
        }

        const field = value[0];
        const column = columns.find((c) => c.dataField === field);
        if (!column) {
          return value;
        }

        const isDate = column.dataType === 'date';
        if (!isDate) {
          return value;
        }

        // apply fix to DATE values (not DATETIME)
        const date = new Date(value[2]);
        value[2] = fixDateTZ(date);
        return value;
      });
      newParams.set('filters', filtersJson);
    } else {
      // No valid filters - remove from URL
      newParams.delete('filters');
    }

    // Replace: true prevents new history entry on each filter change
    setSearchParams(newParams, { replace: true });
  } catch (error) {
    console.error('Failed to write filters to URL', error);
  }
};

const useStateStoring = <T>(
  dataGridId: string,
  columns: ColumnProps<T>[],
  initialState: GridState = {},
) => {
  const { user } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const userId = user?.id ?? '?';
  const key = `${userId}/${STORAGE.DATAGRID}/${dataGridId}`;

  const tempState = useRef<GridState>(initialState);

  useTraceUpdate('state', { key, columns, searchParams });

  const load = useCallback(() => {
    try {
      // Load non-filter state from localStorage (pageSize, columns)
      const persistedState = store.get<GridState>(key, {});

      const urlFilters = readFiltersFromURL(searchParamsRef.current);

      const state: GridState = {
        ...persistedState,
        ...(urlFilters.filterValue ? { filterValue: urlFilters.filterValue } : {}),
        columns: columns
          .map((col) => {
            const tempCol = findCol(tempState.current.columns, col.dataField);
            const persistedCol = findCol(persistedState?.columns, col.dataField);

            return {
              ...tempCol,
              ...persistedCol,
            };
          })
          .filter((col) => col.dataField),
      };

      return state;
    } catch (error) {
      console.error('Failed loading grid state', {
        key,
        error,
      });
      return tempState.current;
    }
  }, [key, columns]);

  const save = useCallback(
    (state: GridState) => {
      try {
        // Save non-filter state to localStorage (pageSize, columns)
        // Filters are managed via URL, not localStorage
        const persistedState = pick(state, persistedKeys);
        persistedState.columns = state.columns?.map((col) => pick(col, persistedColKeys));
        store.set(key, persistedState);
      } catch (error) {
        console.error('Failed saving data grid state', { key, state, error });
      } finally {
        tempState.current = {
          ...tempState.current,
          ...state,
        };
      }
    },
    [key],
  );

  const updateFilters = useMemo(() => {
    return debounce((filterValue: Filter | null | undefined) =>
      writeFiltersToURL(filterValue, searchParamsRef.current, setSearchParams, columns),
    );
  }, [columns]);

  return {
    load,
    save,
    updateFilters,
  };
};

export default useStateStoring;
